import { useState, useCallback } from 'react';
import { scanDocument, DocumentOcrConfig, DocumentOcrResult } from '@/lib/document-ocr';
import { OcrContext } from '@/lib/convex';

export type OcrStatus = 'idle' | 'scanning' | 'success' | 'error';

const MAX_ATTEMPTS = 3;

export function useDocumentOcr() {
  const [status, setStatus] = useState<OcrStatus>('idle');
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<DocumentOcrResult | null>(null);
  const [attemptCount, setAttemptCount] = useState(0);

  const scan = useCallback(async (file: File, config: DocumentOcrConfig, ctx?: OcrContext, clientName?: string, language?: string) => {
    setStatus('scanning');
    setProgress(0);
    setResult(null);

    const nextAttemptCount = attemptCount + 1;
    setAttemptCount(nextAttemptCount);

    const scanResult = await scanDocument(file, config, (p) => setProgress(p), ctx, clientName, language);

    // ── La nature se redemande, la suffisance se note ──────────────────────
    //
    // Deux questions très différentes se cachaient derrière le même refus.
    //
    // « Ce n'est pas un passeport » porte sur la NATURE du document. Le client
    // s'est trompé de fichier, il corrige en dix secondes, et redemander aide
    // vraiment.
    //
    // « Ce permis est expiré », « il manque une page vierge », « ce n'est pas
    // assez récent » portent sur sa SUFFISANCE. Le client n'a rien de mieux dans
    // son tiroir : lui redemander trois fois ne fait pas apparaître un permis
    // valide, ça lui apprend seulement qu'il échoue. Et juger la suffisance
    // d'une pièce est le travail du cabinet — c'est ce que le client paie pour.
    //
    // Relevé sur un dossier réel : une cliente dépose son permis de travail, le
    // BON document, expiré. Refusé. Elle en conclut que ce n'est pas ce qu'on lui
    // demande et dépose autre chose, une lettre de refus. Le premier refus avait
    // causé le second. Accepté avec une note, le cabinet aurait lu « permis
    // expiré depuis le 15 juillet » — ce qu'il doit savoir et traiter de toute
    // façon.
    //
    // C'est le traitement qu'on réservait déjà au nom qui ne correspond pas,
    // étendu à tout ce qui relève du droit plutôt que de la nature du document.
    // Les champs extraits sont gardés : c'est le bon document, de la bonne
    // personne, seulement imparfait.
    if (scanResult.error === 'NAME_MISMATCH' || scanResult.error === 'REQUIREMENTS_NOT_MET') {
      setResult({ ...scanResult, success: true });
      setStatus('success');
    } else if (scanResult.error === 'WRONG_DOCUMENT' && nextAttemptCount >= MAX_ATTEMPTS) {
      // Le mauvais type finit par passer lui aussi — personne ne reste bloqué —
      // mais il ne préremplit rien : il n'y a rien de fiable à tirer d'un
      // document qui n'est pas celui qu'on demandait.
      setResult({
        ...scanResult,
        success: true,
        extractedFields: {},
        displayFields: [],
      });
      setStatus('success');
    } else {
      setResult(scanResult);
      setStatus(scanResult.success ? 'success' : 'error');
    }
  }, [attemptCount]);

  // Retry: go back to idle without resetting attempt count
  const retry = useCallback(() => {
    setStatus('idle');
    setProgress(0);
    setResult(null);
  }, []);

  // Full reset: clears everything including attempt count
  const reset = useCallback(() => {
    setStatus('idle');
    setProgress(0);
    setResult(null);
    setAttemptCount(0);
  }, []);

  const hasRetriesLeft = attemptCount < MAX_ATTEMPTS;

  return { status, progress, result, scan, reset, retry, attemptCount, maxAttempts: MAX_ATTEMPTS, hasRetriesLeft };
}
